import { useCallback, useMemo, createElement } from 'react';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import type { SelectorOption } from '@/components/ui/inputs/selector';
import type { TableColumn } from '@/components/ui/tables/Table';
import { Text } from '@/components/ui/text/Text';
import {
  myTeamTodayEmployeeStatusService,
  type MyTeamTodayEmployeeStatusRow,
  type MyTeamTodayEmployeeStatusCode,
} from '@/api/domains/attendance/myTeam';
import { getEmployeeStatusTextColor } from '@/components/features/attendance/dashboard/myTeamThresholds';


const EMPTY_CELL = '-';

const renderCell = (value: string | null | undefined): string => {
  if (value === null || value === undefined) return EMPTY_CELL;
  const text = value.trim();
  return text === '' ? EMPTY_CELL : text;
};

// The 4 statuses the backend can return, in the same priority order used when
// `orderBy=statusCode` (Ausente, Atraso, Ausente justificado, Presente) — see AC.
const STATUS_CODES: MyTeamTodayEmployeeStatusCode[] = [
  'ABSENT',
  'LATE',
  'JUSTIFIED_ABSENCE',
  'PRESENT',
];

// Column keys map 1:1 to backend order fields, except `rut` (UI convenience name for
// `employeeTaxIdentificationNumber`) — declared explicitly so the backend validator never
// rejects an unmapped orderBy value (see usePaginatedList's orderByMap gotcha).
const ORDER_BY_MAP: Record<string, string> = {
  rut: 'employeeTaxIdentificationNumber',
  status: 'statusCode',
};

export interface MyTeamTodayEmployeeStatusFilters {
  statusCodes: MyTeamTodayEmployeeStatusCode[];
}

export interface UseMyTeamTodayEmployeeStatusListResult {
  rows: MyTeamTodayEmployeeStatusRow[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  loading: boolean;
  onSearch: (text: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  columns: TableColumn<MyTeamTodayEmployeeStatusRow>[];
  statusOptions: SelectorOption[];
  statusFilter: MyTeamTodayEmployeeStatusCode[];
  onStatusFilterChange: (value: string | string[]) => void;
  getRowTextColor: (row: MyTeamTodayEmployeeStatusRow) => string | undefined;
}

// Data hook for the "Mi equipo" dashboard "Situación de asistencia" employee status table
// (ASIS-5408 / REXASIS-007). Recalculates whenever `companyIds`/`employeeIds` change — same
// rule as the 4 summary cards above it — plus its own pagination/search/sort/status filter,
// all delegated to `usePaginatedList` (mandatory for every paginated table in the project).
export function useMyTeamTodayEmployeeStatusList(
  companyIds: number[],
  employeeIds: number[],
): UseMyTeamTodayEmployeeStatusListResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<MyTeamTodayEmployeeStatusFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<MyTeamTodayEmployeeStatusRow> | null> => {
      const response = await myTeamTodayEmployeeStatusService.list(
        {
          page: params.page,
          pageSize: params.pageSize,
          orderBy: params.sort?.key,
          orderDirection: params.sort?.direction,
          companyIds: companyIds.length > 0 ? companyIds : undefined,
          employeeIds: employeeIds.length > 0 ? employeeIds : undefined,
          statusFilter: params.filters.statusCodes.length > 0
            ? params.filters.statusCodes
            : undefined,
          searchText: params.searchText,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('attendance:dashboard.myTeam.employeeStatusList.loadError'));
        return null;
      }

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [companyIds, employeeIds, showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
  } = usePaginatedList<MyTeamTodayEmployeeStatusRow, MyTeamTodayEmployeeStatusFilters>({
    fetchFn,
    initialFilters: { statusCodes: [] },
    initialSort: { key: 'statusCode', direction: 'ASC' },
    orderByMap: ORDER_BY_MAP,
  });

  const onStatusFilterChange = useCallback((value: string | string[]) => {
    const codes = (Array.isArray(value) ? value : value ? [value] : []) as
      MyTeamTodayEmployeeStatusCode[];
    setFilters((prev) => ({ ...prev, statusCodes: codes }));
  }, [setFilters]);

  const statusOptions: SelectorOption[] = useMemo(() => STATUS_CODES.map((code) => ({
    value: code,
    label: translate(`attendance:dashboard.myTeam.employeeStatusList.statuses.${code}`),
  })), [translate]);

  // Figma pinta la fila completa (no solo la celda Estado) con el color del estado — la columna
  // `status` ya trae su propio color vía `render` (Table.utils.resolveCellContent preserva
  // elementos React devueltos por `render`, ignorando `getRowTextColor` ahí), así que esto solo
  // afecta a las demás columnas de texto plano.
  const getRowTextColor = useCallback((row: MyTeamTodayEmployeeStatusRow) => (
    getEmployeeStatusTextColor(row.statusCode)
  ), []);

  const columns: TableColumn<MyTeamTodayEmployeeStatusRow>[] = useMemo(() => [
    {
      key: 'rut',
      header: translate('attendance:dashboard.myTeam.employeeStatusList.columns.rut'),
      sortable: true,
    },
    {
      key: 'employeeName',
      header: translate('attendance:dashboard.myTeam.employeeStatusList.columns.employeeName'),
      sortable: true,
    },
    {
      key: 'status',
      header: translate('attendance:dashboard.myTeam.employeeStatusList.columns.status'),
      sortable: true,
      render: (row) => createElement(
        Text,
        {
          variant: 'body-medium',
          fontFamily: 'montserrat',
          style: { fontWeight: 600 },
          color: getEmployeeStatusTextColor(row.statusCode),
          children: row.status,
        },
      ),
    },
    {
      key: 'entryTime',
      header: translate('attendance:dashboard.myTeam.employeeStatusList.columns.entryTime'),
      sortable: true,
      render: (row) => renderCell(row.entryTime),
    },
    {
      key: 'entryModality',
      header: translate('attendance:dashboard.myTeam.employeeStatusList.columns.entryModality'),
      sortable: true,
      render: (row) => renderCell(row.entryModality),
    },
    {
      key: 'exitTime',
      header: translate('attendance:dashboard.myTeam.employeeStatusList.columns.exitTime'),
      sortable: true,
      render: (row) => renderCell(row.exitTime),
    },
    {
      key: 'exitModality',
      header: translate('attendance:dashboard.myTeam.employeeStatusList.columns.exitModality'),
      sortable: true,
      render: (row) => renderCell(row.exitModality),
    },
    {
      key: 'lateSummary',
      header: translate('attendance:dashboard.myTeam.employeeStatusList.columns.lateSummary'),
      sortable: true,
    },
    {
      key: 'overtimeHours',
      header: translate('attendance:dashboard.myTeam.employeeStatusList.columns.overtimeHours'),
      sortable: true,
    },
  ], [translate]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    columns,
    statusOptions,
    statusFilter: filters.statusCodes,
    onStatusFilterChange,
    getRowTextColor,
  };
}

export default useMyTeamTodayEmployeeStatusList;
